import { useRef } from 'react';
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform,
} from 'framer-motion';
import { cn } from '@rocco/utils/cn';
import { ScrollCue } from '~/ui-rocco/pages/detail-shared';
import {
    ARTICLE_PHOTO_ATTR,
    useArticlePhotoViewer,
} from './ArticlePhotoViewer';
import { Image } from '@rocco/components/image';
import { RevampCategoryPills } from './RevampCategoryPills';
import { useRevampPublishDate } from './RevampPublishDate';
import type { RevampHeaderCategory } from '../type';

/** The tracked caps of the credits line above the headline. */
const META_TYPE =
    'font-sans text-[0.6875rem] font-medium tracking-[0.22em] uppercase';

/** The credited names: 12px uppercase, as the project page sets them. */

/**
 * The nav's link column — Products / Professionals / Projects, at 527px on the
 * 1920 frame. Not the `Products` inside the tagline sentence beside it, which
 * sits at 203px and is a different thing that happens to share a word.
 *
 * Below 1920 the header scales with the fluid root, so a rem tracks it exactly;
 * above, the header centres its 1920 frame and the `max()` follows it out. Same
 * shape as the directory's gutter.
 */
const NAV_COLUMN_X =
    'md:pl-[max(32.9375rem,calc((100%_-_1920px)/2_+_32.9375rem))]';

/** How much taller than the masthead the drifting picture is drawn, in percent. */
const PARALLAX_OVERSCAN = 24;

export interface RevampPremiumTitleSectionProps {
    /** Article headline, set in the large Gestura display face. */
    title: string;
    /** Author name for the "By" credit. */
    writtenBy?: string;
    /** Raw publish date from the loader; formatted here, not by the caller. */
    publishDate?: string;
    /** Estimated reading time, already worded ("9 MIN READ"). */
    readTime?: string;
    /** The article's opening paragraph, set under the headline. */
    standfirst?: string;
    /**
     * The article's subjects, drawn as pills. Its themes where an editor has
     * tagged it, falling back to the categories it is shelved under — an
     * untagged article would otherwise show nothing here.
     */
    tags?: RevampHeaderCategory[];
    /**
     * The professionals the article is about — its own professional first, then
     * the credited practices. Named rather than numbered: the reader is being
     * told whose work this is before they commit to reading about it.
     */
    /**
     * The banner, laid behind the masthead rather than under it. When absent
     * the masthead is type on a white page and the banner follows below.
     */
    backgroundImageUrl?: string;
    /**
     * The library image behind the banner, so a save from the photo viewer
     * pins the photograph rather than the article standing in for it.
     */
    backgroundLibraryImageId?: number;
}

/**
 * Premium article masthead (Figma 2:2796 — "C·03 Article: The Read").
 *
 * A white page rather than a photograph: the credits, headline and standfirst
 * are set as type on the page, and the article's picture follows underneath at
 * full bleed. That inverts what this section used to be — a full-bleed banner
 * with white copy over it — because the premium treatment leads with the
 * writing rather than with the image.
 *
 * The copy sits in a column starting a quarter of the way across, with the
 * monogram parked in the margin beside it. Sizes in rem at the 1920 artboard
 * (÷16), base + md: only, matching the article-revamp siblings.
 */
export const RevampPremiumTitleSection = ({
    title,
    writtenBy,
    publishDate,
    readTime,
    standfirst,
    tags = [],
    backgroundImageUrl,
    backgroundLibraryImageId,
}: RevampPremiumTitleSectionProps) => {
    // Localised here for the same hydration reason as the other bylines — see
    // useRevampPublishDate.
    const date = useRevampPublishDate(publishDate);

    const overImage = !!backgroundImageUrl;
    const sectionRef = useRef<HTMLElement>(null);
    const reduceMotion = useReducedMotion() ?? false;
    const openPhotoViewer = useArticlePhotoViewer();
    // Measured across the whole time the masthead is on screen, so the drift is
    // continuous rather than starting once it is already in view.
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end start'],
    });
    // The picture is PARALLAX_OVERSCAN taller than the masthead and travels
    // half that each way, so the frame stays covered at both ends.
    const parallaxY = useTransform(
        scrollYProgress,
        [0, 1],
        [`-${PARALLAX_OVERSCAN / 2}%`, `${PARALLAX_OVERSCAN / 2}%`],
    );
    // Two inks per ground: the ordinary one, and the stepped-back one the
    // labels and the date use. Over a photograph both go up, not down.
    const mutedInk = overImage ? 'text-white/70' : 'text-[#767676]';

    return (
        <section
            ref={sectionRef}
            className={cn(
                'relative w-full pt-10 md:pt-0',
                overImage
                    ? cn(
                          'bg-[#131313] pb-[3.5rem] text-white md:pb-0',
                          // The directory's featured slide, to the pixel: the
                          // two are the same gesture on the two pages, and a
                          // banner that is nearly-but-not-quite the same height
                          // reads as a mistake.
                          'md:h-[min(71.51vw,85.8125rem)]',
                      )
                    : 'bg-white text-[#181818]',
            )}
        >
            {overImage ? (
                // Deliberately NOT isolated. An isolated group is composited on
                // its own and cannot be sampled across, so the header's glass
                // monogram had nothing to blur but the flat ground behind this
                // — the photograph was invisible to it. The scrim below is plain
                // alpha, not a blend mode, so it needs no isolation to behave.
                // A press on the banner opens it, which is what every other
                // picture in the article does. On the layer rather than a
                // button around it: a button the size of the masthead would
                // swallow the title's own links and be one enormous tab stop.
                // Keyboard readers reach the banner by paging the viewer open
                // from any body picture.
                <div
                    className="absolute inset-0 cursor-zoom-in overflow-hidden"
                    onClick={(event) => {
                        const image =
                            event.currentTarget.querySelector<HTMLElement>(
                                `[${ARTICLE_PHOTO_ATTR}]`,
                            );
                        if (image) openPhotoViewer?.(image);
                    }}
                >
                    <motion.div
                        style={reduceMotion ? undefined : { y: parallaxY }}
                        className="absolute inset-x-0 top-[-12%] bottom-[-12%]"
                    >
                        <Image
                            src={backgroundImageUrl}
                            alt=""
                            fill={true}
                            fit="cover"
                            loading="eager"
                            sizes="100vw"
                            className="absolute inset-0"
                            // In the article's photo viewer with the body's
                            // pictures: the banner is the article's opening
                            // image, so paging should reach it.
                            {...{ [ARTICLE_PHOTO_ATTR]: '' }}
                            data-article-photo-src={backgroundImageUrl}
                            data-article-photo-caption=""
                            data-article-photo-id={
                                backgroundLibraryImageId ?? undefined
                            }
                        />
                    </motion.div>
                    {/* Enough ground for white type across the whole masthead,
                        deepest at the foot where the columns sit. */}
                    <div
                        aria-hidden={true}
                        className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/75"
                    />
                </div>
            ) : null}

            {/* The same cue the Profiles hero carries, at the foot of the
                first screen rather than of the banner — the banner is taller
                than the window, so its own foot is a screen further down. Over
                the image only: the white variant has no full-screen picture to
                scroll past. */}
            {overImage ? (
                <button
                    type="button"
                    onClick={(event) => {
                        // The banner is taller than the window, so the next
                        // screen down is what the reader wants, not the end of
                        // the picture.
                        const hero = event.currentTarget.closest('section');
                        const next =
                            hero?.nextElementSibling as HTMLElement | null;
                        if (next) {
                            next.scrollIntoView({ behavior: 'smooth' });
                        } else {
                            window.scrollBy({
                                top: window.innerHeight,
                                behavior: 'smooth',
                            });
                        }
                    }}
                    aria-label="Scroll to the article"
                    className="absolute left-1/2 z-10 -translate-x-1/2 cursor-pointer transition-opacity hover:opacity-70 max-md:hidden md:top-[calc(100svh_-_9rem)]"
                >
                    <ScrollCue />
                </button>
            ) : null}

            {/* Over the image the masthead starts below the nav it shares the
                frame with; on the white page it rides up level with the foot
                of the header's own monogram. */}
            <div
                className={cn(
                    'relative px-4.5',
                    overImage
                        ? cn(
                              // Centred in the window, not in the banner: the
                              // banner is taller than the window, so centring
                              // on it would put the group below the fold. Half
                              // the first screen, pulled back by half the
                              // group's own height, then dropped 50px so the
                              // copy sits just under the centre line rather
                              // than on it.
                              'md:absolute md:inset-x-0 md:top-[calc(50svh_+_3.125rem)] md:-translate-y-1/2',
                              // Ranged centre on the page rather than left to
                              // the nav column: over a full-bleed photograph
                              // there is no column of content for the type to
                              // align to, so the frame itself is the measure.
                              'md:px-[3.125rem] md:text-center',
                          )
                        : cn(
                              'md:px-0 md:pr-[3.1625rem]',
                              NAV_COLUMN_X,
                              'md:-mt-[2.875rem]',
                          ),
                )}
            >
                {/*
                 * Author, date and reading time on one line. The author is the
                 * page's own ink and the other two step back — they answer
                 * "how long is this" rather than "whose is this".
                 */}
                <div
                    className={cn(
                        'flex flex-wrap items-baseline gap-x-[2.75rem] gap-y-2',
                        META_TYPE,
                        overImage && 'md:justify-center',
                    )}
                >
                    {writtenBy ? <span>By {writtenBy}</span> : null}
                    {date?.label ? (
                        <span className={mutedInk}>{date.label}</span>
                    ) : null}
                    {readTime ? (
                        <span className={mutedInk}>{readTime}</span>
                    ) : null}
                </div>

                <h1
                    className={cn(
                        // Wrapped at roughly 65% of the page rather than the
                        // full measure: at this size a line running the width
                        // of the frame is further than the eye tracks back.
                        'm-0 mt-[1.125rem] font-gestura text-[2.5rem] leading-none font-normal tracking-[-0.03em] md:max-w-[80rem] md:text-[5.1333rem]',
                        overImage && 'md:mx-auto',
                        overImage ? 'text-white' : 'text-black',
                    )}
                >
                    {title}
                </h1>

                {standfirst ? (
                    // Gestura, the face the headline above it is set in: the
                    // standfirst belongs to the headline's group rather than to
                    // the body copy, which is a grotesque.
                    <p
                        className={cn(
                            'm-0 mt-[2rem] font-gestura text-[1.25rem] leading-[1.7] font-normal md:max-w-[47.5rem] md:text-[1.375rem] md:leading-[2.125rem]',
                            overImage && 'md:mx-auto',
                            overImage ? 'text-white' : 'text-black',
                        )}
                    >
                        {standfirst}
                    </p>
                ) : null}

                {/* The tags, centred under the picture with the title
                    group. They were one of three columns spread across a rule;
                    the professionals moved out (the article's own credits
                    carry them at the foot, where there is room for the whole
                    list) and the reader's actions moved into the island, so
                    what is left is a single label and its pills. */}
                {tags.length > 0 ? (
                    // No "Tags" label over them: the pills are plainly tags,
                    // and the word was the only thing between the standfirst
                    // and them.
                    <div className="mt-[2.5rem] flex flex-col items-center">
                        <RevampCategoryPills
                            categories={tags}
                            inverse={overImage}
                            className="justify-center"
                        />
                    </div>
                ) : null}
            </div>
        </section>
    );
};
